<script setup lang="ts">
  import type { Column, Table } from '@tanstack/vue-table';
  import { computed } from 'vue';

  import { Button } from '@ui/components/button';
  import { Checkbox } from '@ui/components/checkbox';
  import { Popover, PopoverContent, PopoverTrigger } from '@ui/components/popover';

  interface DataTableViewOptionsProps {
    table: Table<any>;
  }

  const props = defineProps<DataTableViewOptionsProps>();

  const columns = computed(() =>
    props.table
      .getAllColumns()
      .filter((column) => typeof column.accessorFn !== 'undefined' && column.getCanHide()),
  );

  const getColumnLabel = (column: Column<any, unknown>): string => {
    if ((column.columnDef.meta as any)?.displayName)
      return (column.columnDef.meta as any).displayName;
    if (typeof column.columnDef.header === 'function') {
      try {
        const node = column.columnDef.header({ column } as any);
        const title =
          node?.props?.title ??
          (Array.isArray(node?.children)
            ? node.children[0]?.props?.title
            : node?.children?.props?.title);
        if (title) return title;
      } catch {}
    }
    return column.id.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');
  };
</script>

<template>
  <Popover>
    <PopoverTrigger as-child>
      <Button variant="outline" size="sm" class="h-8" aria-label="Toggle columns">
        <i class="far fa-columns-3 text-xs" />
      </Button>
    </PopoverTrigger>
    <PopoverContent align="end" class="w-fit max-w-48 p-3">
      <div class="flex flex-col gap-2">
        <label
          v-for="column in columns"
          :key="column.id"
          class="flex items-center gap-2 cursor-pointer select-none"
        >
          <Checkbox
            :model-value="column.getIsVisible()"
            @update:model-value="(value) => column.toggleVisibility(!!value)"
          />
          <span class="text-xs">{{ getColumnLabel(column) }}</span>
        </label>
      </div>
    </PopoverContent>
  </Popover>
</template>
